import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import SeoHeader from "../../../components/seo/SeoHeader.jsx";
import SeoContactSection from "../../../components/seo/SeoContactSection.jsx";
import SeoWorkSteps from "../../../components/seo/SeoWorkSteps.jsx";
import CountryBanner from "../../../components/CountryBanner.jsx";

const sections = [
  {
    id: "start",
    label: "Menyoversettelse for restauranter bør være enkel",
  },
  {
    id: "paper",
    label: "Papirmenuer på flere språk",
  },
  {
    id: "ai",
    label: "Menyoversettelse med AI",
  },
  {
    id: "localization",
    label: "Lokalisering av restaurantmeny",
  },
  {
    id: "comparison",
    label: "Fordeler og ulemper med hver løsning",
  },
  {
    id: "recommendation",
    label: "Hva bør en restauranteier velge?",
  },
  {
    id: "bestplace",
    label: "Hvordan Best Place hjelper med oversatte menyer",
  },
  {
    id: "faq",
    label: "FAQ om oversettelse av restaurantmenyer",
  },
];

const CALLBACK_API_ORIGIN =
  typeof window !== "undefined" &&
  (window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1")
    ? "http://localhost:3030"
    : "";

function VisualIdea({ children }) {
  return (
    <div className="seoVisualIdea" aria-label="Visuell idé">
      <div className="seoVisualIdea__icon" aria-hidden="true">
        ✦
      </div>

      <div>
        <strong>Idé til illustrasjon:</strong>
        <p>{children}</p>
      </div>
    </div>
  );
}

function setMetaDescription(content) {
  if (typeof document === "undefined") return;

  let meta = document.querySelector('meta[name="description"]');

  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute("name", "description");
    document.head.appendChild(meta);
  }

  meta.setAttribute("content", content);
}

export default function MenuTranslationForRestaurantsNo() {
  useEffect(() => {
    document.documentElement.setAttribute("lang", "no");

    document.title =
      "Menyoversettelse for restauranter: AI, papir eller QR-meny? | Best Place";

    setMetaDescription(
      "Lær hvordan restauranter kan oversette menyer: papirmenuer, AI-oversettelse, lokalisering og flerspråklige QR-menyer for internasjonale gjester."
    );
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <main className="qrArticle">
      <SeoHeader
        lang="no"
        langLinks={{
          de: "/de/speisekarte-ubersetzen-restaurant",
          en: "/en/menu-translation-for-restaurants",
          no: "/no/oversette-restaurantmeny",
        }}
      />

      <section className="qrArticle__hero">
        <div className="qrArticle__heroInner">
          <h1 className="qrArticle__heroTitle">
            <span>
              Menyoversettelse for restauranter: papir, AI eller digital
              QR-meny?
            </span>
          </h1>

          <div className="qrArticle__topsection">
            <div className="qrArticle__topsection--img-wrapper">
              <div className="qrArticle__topsection--img"></div>
            </div>

            <div className="qrArticle__topsection--text-wrapper">
              <p className="seoHero__lead">
                Et av de største problemene for utenlandske gjester på
                restaurant er menyteksten. Hvis du leter etter en løsning for
                restauranten din, er det viktig å forstå forskjellen mellom
                papir, AI, profesjonell oversettelse og flerspråklige QR-menyer.
              </p>

              <p className="seoHero__lead">
                Å oversette en restaurantmeny høres enkelt ut. Ta teksten,
                oversett den, publiser den. Ferdig. I en restaurant er det
                sjelden så enkelt.
              </p>

              <p className="seoHero__text">
                Retter har ingredienser. Noen navn er lokale. Allergener betyr
                mye. Gjester vil forstå hva de bestiller. En god oversettelse
                gjør menyen tydeligere — ikke mer komplisert.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="countryBannerBlock">
        <CountryBanner
          reverse
          ariaLabel={"Countries and languages"}
        />
      </div>

      <SeoContactSection
        lang="no"
        apiOrigin={CALLBACK_API_ORIGIN}
        title="Trenger du hjelp med en oversatt restaurantmeny?"
      />

      <section className="articls">
        <h2>Innhold i artikkelen</h2>

        <nav className="qrArticle__nav" aria-label="Artikkelnavigasjon">
          <ol className="qrArticle__navList">
            {sections.map((item, index) => (
              <li key={item.id} className="qrArticle__navItem">
                <button
                  type="button"
                  className="qrArticle__navButton"
                  onClick={() => scrollToSection(item.id)}
                >
                  <span className="qrArticle__navNumber">
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="qrArticle__navLabel">{item.label}</span>
                </button>
              </li>
            ))}
          </ol>
        </nav>
      </section>

      <article>
        <section id="start" className="acquaintance">
          <h2>Menyoversettelse for restauranter bør være enkel</h2>

          <p>
            Mange restauranteiere begynner å tenke på oversettelse når gjester
            stiller for mange spørsmål. Gjesten forstår ikke retten. Teamet må
            forklare det samme igjen og igjen. På et tidspunkt blir det
            tydelig: menyen trenger et annet språk.
          </p>

          <p>
            Det finnes flere måter å løse dette på. Du kan bruke en trykt meny
            på flere språk, bruke AI-oversettelse, bestille profesjonell
            lokalisering eller bruke en digital{" "}
            <Link
              to="/no/flerspraklig-meny-for-restauranter"
              className="seoAccentLink"
            >
              flerspråklig meny for restauranter
            </Link>{" "}
            med språkvalg.
          </p>

          <p>
            Ikke alle løsninger passer alle restauranter. En liten kafé trenger
            kanskje bare engelsk. En restaurant nær hoteller kan trenge flere
            språk. Et turiststed kan trenge enda mer. Målet er ikke flest mulig
            språk. Målet er å velge de riktige språkene for ekte gjester.
          </p>
        </section>

        <section id="paper" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Papirmenuer på flere språk</h2>

            <p>
              En klassisk papirmeny er fortsatt viktig. Mange gjester er vant
              til papir. Men å oversette papirmenuer kan raskt bli krevende. Det
              kan virke som om det holder å trykke menyen på to eller tre språk.
              For noen restauranter er det nok. Men allerede ett ekstra språk
              gjør menyen større, og med flere språk kan det bli vanskelig å
              holde alt oversiktlig.
            </p>

            <p>
              En annen løsning er å ha separate menyer på ulike språk. Hvis en
              italiensk gjest kommer, får han eller hun den italienske
              versjonen. Hvis en tysk gjest kommer, får han eller hun den tyske
              versjonen. Dette kan være behagelig for gjesten, men
              vanskelighetene øker tydelig når:
            </p>

            <ul className="qrArticle__block-li">
              <li className="text__seo-li">
                Flere språk gjør trykte menyer lengre.
              </li>

              <li className="text__seo-li">
                Oppdateringer koster tid og penger til trykk.
              </li>

              <li className="text__seo-li">
                Liten tekst blir vanskelig å lese.
              </li>

              <li className="text__seo-li">
                Gamle oversettelser blir ofte brukt for lenge.
              </li>

              <li className="text__seo-li">
                Allergener og ingredienser må kontrolleres nøye på hvert språk.
              </li>
            </ul>

            <p>
              Papir er ikke feil. Det er bare lite fleksibelt. Det fungerer best
              for korte menyer som sjelden endres.
            </p>
          </div>
        </section>

        <section id="ai" className="acquaintance">
          <h2>Menyoversettelse med AI</h2>

          <p>
            Vi er vant til at tekstoversettelse med kunstig intelligens har
            blitt svært enkel. Men når det gjelder restaurantmenyer, er det ikke
            alltid så rett frem. AI kan raskt lage et første utkast og skrive
            enkle beskrivelser, men vanskeligheter oppstår ofte ved nasjonale
            retter, regionale navn, nye produkter og ingredienser.
          </p>

          <p>
            En praktisk måte å bruke AI på er enkel: bruk AI til første utkast.
            Deretter må navn på retter, ingredienser, allergener og regionale
            særtrekk kontrolleres. Først etterpå bør teksten publiseres.
          </p>

          <p>
            Det finnes også AI-baserte tjenester som kan brukes til å vise
            restaurantmenyer på flere språk. Gjestene velger språk i tjenesten,
            og AI oversetter automatisk innholdet.
          </p>

          <VisualIdea>
            Kort sagt: AI-oversettelse er rask og ganske enkel, men
            restauranten må ta høyde for at enkelte deler av menyteksten ikke
            blir riktig oversatt på gjestens språk.
          </VisualIdea>
        </section>

        <section id="localization" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Lokalisering av restaurantmeny</h2>

            <p>
              Hvis du vil at menyoversettelsen skal være mer enn bare å bytte ut
              ord, må du se på lokalisering. En god menyoversettelse hjelper
              gjester med å forstå hva de vil prøve, selv når de ikke kjenner
              det lokale kjøkkenet.
            </p>

            <p>
              Eksempel: navnet på en regional rett kan være åpenbart for lokale
              gjester. For turister betyr det kanskje ingenting. Da er en kort
              beskrivelse bedre enn en direkte oversettelse. Hva er inni?
              Hvordan serveres retten? Er den sterk? Er den vegetarisk? Finnes
              det allergener?
            </p>

            <p>
              Det finnes tjenester som tilbyr profesjonell oversettelse av
              restaurantmenyer. Dette kan være en mer omfattende prosess, fordi
              det ofte krever personer som både forstår språk og
              matbeskrivelser.
            </p>
          </div>
        </section>

        <section id="comparison" className="acquaintance">
          <h2>Fordeler og ulemper med hver løsning</h2>

          <p>
            Det finnes ingen perfekt løsning for alle restauranter. Det riktige
            valget avhenger av hvor ofte menyen endres og hvor mange
            internasjonale gjester restauranten har.
          </p>

          <div className="seoComparison">
            <div className="seoComparison__col">
              <h3>Svakere ved hyppige oppdateringer</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Papirmenuer må trykkes på nytt etter endringer.
                </li>

                <li className="text__seo-li">
                  Kamera- og oversettelsesapper gir restauranten lite kontroll.
                </li>

                <li className="text__seo-li">
                  AI-oversettelser må kontrolleres.
                </li>

                <li className="text__seo-li">
                  Separate PDF-filer blir vanskelige å administrere.
                </li>

                <li className="text__seo-li">
                  Flere språkversjoner er vanskelige å holde oppdatert.
                </li>
              </ul>
            </div>

            <div className="seoComparison__col seoComparison__col--highlight">
              <h3>Bedre for fleksible restauranter</h3>

              <ul className="qrArticle__block-li">
                <li className="text__seo-li">
                  Én digital meny kan inneholde flere språk.
                </li>

                <li className="text__seo-li">
                  Gjester velger språk på smarttelefonen.
                </li>

                <li className="text__seo-li">
                  Priser og beskrivelser kan oppdateres online.
                </li>

                <li className="text__seo-li">
                  Ingredienser og allergener kan kontrolleres per språk.
                </li>

                <li className="text__seo-li">
                  Den samme QR-koden kan brukes videre etter endringer.
                </li>
              </ul>
            </div>
          </div>
        </section>

        <section id="recommendation" className="qrArticle__quizWave">
          <div className="qrArticle__quizInner">
            <h2>Hva bør en restauranteier velge?</h2>

            <p>
              Hvis menyen nesten aldri endres og du bare trenger ett ekstra
              språk, kan en trykt meny være nok. Hold det enkelt.
            </p>

            <p>
              Hvis restauranten ofte endrer priser, retter eller dagens tilbud,
              er en digital meny vanligvis mer praktisk. Du trenger ikke trykke
              på nytt hver gang.
            </p>

            <p>
              Hvis du betjener turister, hotellgjester eller internasjonale
              besøkende, start med språkene du faktisk hører i restauranten.
              Engelsk er ofte første steg. Deretter kan du legge til neste språk
              basert på ekte etterspørsel.
            </p>

            <p>
              Et godt neste steg er å teste en{" "}
              <Link
                to="/no/flerspraklig-meny-for-restauranter"
                className="seoAccentLink"
              >
                flerspråklig QR-meny
              </Link>
              . Sjekk den på en smarttelefon. Spør teamet. Se om gjester
              forstår retter raskere. Utvid deretter.
            </p>
          </div>
        </section>

        <div id="bestplace" className="qrArticle__anchorTarget">
          <SeoWorkSteps lang="no" />
        </div>

        <section className="acquaintance">
          <h2>Hvordan Best Place hjelper med oversatte menyer</h2>

          <p>
            Best Place hjelper restauranteiere med å lage en digital meny på
            flere språk. Restauranten beholder én tydelig struktur. Gjesten
            åpner menyen med en QR-kode og velger språket han eller hun trenger.
          </p>

          <p>
            Dette er nyttig når en restaurant vil se mer profesjonell ut for
            internasjonale gjester, redusere gjentatte spørsmål og oppdatere
            menyen uten nye trykkfiler.
          </p>

          <p>
            Du kan starte fra{" "}
            <Link to="/no" className="seoAccentLink">
              Best Place-startsiden
            </Link>
            , lage en enkel meny og teste den direkte på en ekte smarttelefon.
          </p>
        </section>

        <section id="faq" className="acquaintance">
          <h2>FAQ om oversettelse av restaurantmenyer</h2>

          <div className="seoFaq">
            <details>
              <summary>Hva betyr oversettelse av restaurantmeny?</summary>

              <p>
                Det betyr å oversette navn på retter, beskrivelser, kategorier,
                priser, ingredienser og viktige merknader til et annet språk,
                slik at gjester forstår menyen lettere.
              </p>
            </details>

            <details>
              <summary>
                Hvordan oversetter jeg en kafémeny til engelsk riktig?
              </summary>

              <p>
                Start med de viktigste produktene. Ikke oversett bare navn. Legg
                til korte beskrivelser, ingredienser, størrelser, melketyper,
                allergener og ekstra valg der det hjelper gjesten å bestemme
                seg.
              </p>
            </details>

            <details>
              <summary>
                Kan AI automatisk oversette ingredienser og retter?
              </summary>

              <p>
                Ja, AI kan lage et første utkast. Men ingredienser, allergener,
                regionale uttrykk og viktige merknader bør alltid kontrolleres.
              </p>
            </details>

            <details>
              <summary>Hva betyr lokalisering av restaurantmeny?</summary>

              <p>
                Lokalisering betyr at menyen ikke bare oversettes ord for ord.
                Beskrivelsen tilpasses slik at gjester fra andre land faktisk
                forstår retten.
              </p>
            </details>

            <details>
              <summary>
                Er en flerspråklig QR-meny bedre enn flere PDF-er?
              </summary>

              <p>
                I mange tilfeller, ja. En flerspråklig QR-meny er enklere å
                oppdatere, renere på smarttelefon og mer fleksibel enn flere
                separate PDF-filer.
              </p>
            </details>

            <details>
              <summary>
                Hvilket språk bør en restaurant legge til først?
              </summary>

              <p>
                Start med språket de internasjonale gjestene dine bruker oftest.
                For mange restauranter er engelsk det første nyttige
                tilleggsspråket.
              </p>
            </details>
          </div>
        </section>
      </article>
    </main>
  );
}